import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import CTASection from "../components/home/CTASection";
import useReveal from "../hooks/useReveal";
import api, { imageUrl } from "../lib/api";
import { ArrowRight, Clock, Search } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  useReveal([posts, active]);

  useEffect(() => {
    api.get("/blog/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = active === "all" ? "/blog" : `/blog?category=${active}`;
    api.get(url).then((r) => setPosts(r.data)).catch(() => setPosts([])).finally(() => setLoading(false));
  }, [active]);

  const activeCat = categories.find((c) => c.slug === active);

  return (
    <>
      <Seo title="Renovation Blog & Guides" path="/blog"
        description="Expert renovation guides for Edmonton homeowners — planning, budgeting, basements, kitchens, bathrooms, permits and more from Classic Interior Renovations." />

      {/* HERO */}
      <section className="pt-36 pb-16 bg-[#0f2740] text-white">
        <div className="container-x">
          <div className="flex items-center gap-3 mb-5 reveal">
            <span className="h-px w-10 bg-[#2f9fe6]" />
            <span className="text-[#7fb8de] tracking-[0.26em] text-xs font-medium uppercase">Renovation Insights</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl font-bold mb-5 max-w-3xl reveal">Edmonton Renovation Blog &amp; Guides</h1>
          <p className="text-[#a9c6dd] text-lg max-w-2xl reveal">Practical, local advice to help you plan, budget and build your renovation with confidence.</p>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="sticky top-[64px] z-30 bg-white/95 backdrop-blur border-b border-[#eef1f4]">
        <div className="container-x py-4 flex gap-2 overflow-x-auto no-scrollbar">
          <button onClick={() => setParams({})} data-testid="cat-all" className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === "all" ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740] hover:bg-[#e3e8ed]"}`}>All Articles</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setParams({ category: c.slug })} data-testid={`cat-${c.slug}`} className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === c.slug ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740] hover:bg-[#e3e8ed]"}`}>
              {c.name}{c.count > 0 && <span className="ml-1.5 opacity-60">{c.count}</span>}
            </button>
          ))}
        </div>
      </section>

      {/* POSTS */}
      <section className="py-20 bg-white min-h-[50vh]">
        <div className="container-x">
          {activeCat && (
            <div className="mb-10 reveal">
              <h2 className="font-display text-2xl font-bold text-[#0f2740]">{activeCat.name}</h2>
              <p className="text-[#6b7683] mt-1">{activeCat.description}</p>
            </div>
          )}
          {loading ? (
            <div className="text-[#6b7683]">Loading articles…</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-10 h-10 text-[#c7ced5] mx-auto mb-3" />
              <p className="text-[#6b7683]">No articles in this category yet — check back soon.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="blog-grid">
              {posts.map((p, i) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`} className={`reveal delay-${(i % 3) + 1} group bg-white rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col`}>
                  <div className="overflow-hidden h-52"><img src={imageUrl(p.cover)} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-[#9aa4af] mb-3">
                      <span className="text-[#2f9fe6] font-semibold uppercase tracking-wide">{(categories.find((c) => c.slug === p.category) || {}).name || "Guide"}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.read_time} min</span>
                    </div>
                    <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2 leading-snug group-hover:text-[#2f9fe6] transition-colors">{p.title}</h3>
                    <p className="text-[#6b7683] text-sm leading-relaxed flex-1">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-[#2f9fe6] font-semibold text-sm mt-4">Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
}
